import { useEffect, useState } from "react";
import { X, Copy, Check, Twitter, Facebook, Mail, MessageCircle, MessageSquare, Send, Linkedin, Instagram } from "lucide-react";
import { toast } from "sonner";

// Global event name — `share.js#share(payload)` dispatches this, ShareMenu listens.
const OPEN_EVENT = "syndafam-share-open";

// URL builders for each destination. Instagram has NO public share URL for
// feeds/DMs, so we fall back to a "Copy Link" hint that guides the user.
function buildTargets(payload) {
  const url = encodeURIComponent(payload.url);
  const text = encodeURIComponent(payload.text || payload.title || "");
  const title = encodeURIComponent(payload.title || "SyndaFam");
  return [
    {
      id: "copy",
      label: "Copy Link",
      Icon: Copy,
      color: "#FCA311",
      copy: true,
    },
    {
      id: "twitter",
      label: "X / Twitter",
      Icon: Twitter,
      color: "#FFFFFF",
      href: `https://twitter.com/intent/tweet?url=${url}&text=${text}`,
    },
    {
      id: "facebook",
      label: "Facebook",
      Icon: Facebook,
      color: "#1877F2",
      href: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
    },
    {
      id: "messenger",
      label: "Messenger",
      Icon: MessageCircle,
      color: "#00B2FF",
      href: `https://www.facebook.com/dialog/send?link=${url}&app_id=966242223397117&redirect_uri=${url}`,
    },
    {
      id: "whatsapp",
      label: "WhatsApp",
      Icon: MessageCircle,
      color: "#25D366",
      href: `https://wa.me/?text=${text}%20${url}`,
    },
    {
      id: "instagram",
      label: "Instagram",
      Icon: Instagram,
      color: "#E4405F",
      // Instagram doesn't support direct share URLs — copy link + toast hint.
      copy: true,
      hint: "Link copied — paste it into an Instagram DM or Story.",
    },
    {
      id: "sms",
      label: "iMessage / SMS",
      Icon: MessageSquare,
      color: "#34C759",
      href: `sms:?&body=${text}%20${url}`,
    },
    {
      id: "linkedin",
      label: "LinkedIn",
      Icon: Linkedin,
      color: "#0A66C2",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
    },
    {
      id: "telegram",
      label: "Telegram",
      Icon: Send,
      color: "#26A5E4",
      href: `https://t.me/share/url?url=${url}&text=${text}`,
    },
    {
      id: "email",
      label: "Email",
      Icon: Mail,
      color: "#F4F1DE",
      href: `mailto:?subject=${title}&body=${text}%20${url}`,
    },
  ];
}

// Programmatic API: called by `share.js` — dispatches the open event.
export function openShareMenu(payload) {
  if (!payload || !payload.url) return;
  window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: payload }));
}

export default function ShareMenu() {
  const [payload, setPayload] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const onOpen = (e) => {
      setPayload(e.detail || null);
      setCopied(false);
    };
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, []);

  // ESC to close
  useEffect(() => {
    if (!payload) return;
    const onKey = (e) => { if (e.key === "Escape") setPayload(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [payload]);

  if (!payload) return null;

  const handleTarget = async (target) => {
    if (target.copy) {
      try {
        await navigator.clipboard.writeText(payload.url);
        toast.success(target.hint || "Link copied", {
          description: payload.title ? `${payload.title} — paste anywhere to share.` : "Paste anywhere to share.",
        });
        setCopied(true);
        setTimeout(() => { setPayload(null); }, 900);
      } catch {
        toast.error("Couldn't copy — long-press the link instead.");
      }
      return;
    }
    if (target.href) {
      // Open the destination in a new tab; email/sms use protocol handlers.
      const proto = target.href.split(":", 1)[0];
      if (proto === "mailto" || proto === "sms") {
        window.location.href = target.href;
      } else {
        window.open(target.href, "_blank", "noopener,noreferrer,width=680,height=580");
      }
      setPayload(null);
    }
  };

  const targets = buildTargets(payload);

  return (
    <div
      className="fixed inset-0 z-[210] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-md p-0 sm:p-6"
      onClick={() => setPayload(null)}
      role="dialog"
      aria-label="Share"
      data-testid="share-menu-overlay"
    >
      <div
        className="w-full sm:max-w-md bg-[#0B0B0C] border-t-2 sm:border-2 border-[#E63946]/50 sm:rounded-2xl shadow-[0_-20px_60px_rgba(230,57,70,0.35)] p-5 sm:p-6"
        onClick={(e) => e.stopPropagation()}
        data-testid="share-menu-panel"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-5">
          <div className="min-w-0">
            <div className="font-mono text-[10px] tracking-[0.4em] text-[#FCA311]">// SHARE VIA</div>
            <div className="font-display text-2xl mt-1 truncate" data-testid="share-menu-title">
              {payload.title || "SyndaFam"}<span className="text-[#E63946]">.</span>
            </div>
          </div>
          <button
            onClick={() => setPayload(null)}
            className="w-9 h-9 rounded-full hover:bg-white/10 flex items-center justify-center text-white/70 hover:text-white transition-colors flex-shrink-0"
            aria-label="Close share menu"
            data-testid="share-menu-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* URL preview */}
        <div
          className="mb-5 font-mono text-[10px] text-white/50 truncate px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10"
          title={payload.url}
        >
          {payload.url}
        </div>

        {/* Platform grid */}
        <div className="grid grid-cols-4 gap-2 sm:gap-3">
          {targets.map(({ id, label, Icon, color }) => (
            <button
              key={id}
              onClick={() => handleTarget(targets.find((t) => t.id === id))}
              className="flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl bg-white/[0.03] hover:bg-white/[0.08] border border-white/10 hover:border-[#E63946]/60 transition-all group"
              data-testid={`share-menu-${id}`}
              aria-label={`Share via ${label}`}
            >
              <div
                className="w-11 h-11 rounded-full flex items-center justify-center transition-transform group-hover:scale-110"
                style={{
                  border: `2px solid ${color}`,
                  boxShadow: `0 0 12px ${color}55`,
                }}
              >
                {id === "copy" && copied ? (
                  <Check className="w-4 h-4" style={{ color }} />
                ) : (
                  <Icon className="w-4 h-4" style={{ color }} />
                )}
              </div>
              <div className="font-mono text-[9px] tracking-widest text-white/70 text-center leading-tight">
                {label.toUpperCase()}
              </div>
            </button>
          ))}
        </div>

        <div className="mt-4 text-center font-mono text-[9px] tracking-[0.35em] text-white/25">
          TAP OUTSIDE OR PRESS ESC TO CLOSE
        </div>
      </div>
    </div>
  );
}
